import React, { useEffect, useState, useCallback } from 'react';
import {
  Table, Input, Modal, Form, message, Button, Breadcrumb, Grid, Card, Row, Col,
  Statistic, Tag, Tooltip, Switch, Space, Divider, DatePicker
} from 'antd';
import { citiesService } from '../../common/services';
import {
  EditOutlined, DeleteOutlined, RightOutlined, SearchOutlined,
  GlobalOutlined, CalendarOutlined, PlusOutlined, CheckOutlined,
  CloseOutlined, StarOutlined, StarFilled, KeyOutlined, ApiOutlined,
  DatabaseOutlined
} from '@ant-design/icons';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';

const Cities = () => {
  const [cities, setCities] = useState([]);
  const [isLoading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateRange, setDateRange] = useState(null);
  const [stats, setStats] = useState({ total: 0, active: 0, inactive: 0 });

  // Modal d'ajout/édition
  const [isModalOpen, setModalOpen] = useState(false);
  const [selectedCity, setSelectedCity] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [form] = Form.useForm();

  const screens = Grid.useBreakpoint();
  const isMobile = !screens.sm;
  const controlSize = isMobile ? 'small' : 'middle';

  const calculateStats = useCallback((data) => {
    const active = data.filter(c => c.isActive !== false).length;
    setStats({
      total: data.length,
      active,
      inactive: data.length - active
    });
  }, []);

  useEffect(() => {
    fetchCities();
  }, []);

  const fetchCities = async () => {
    setLoading(true);
    try {
      const response = await citiesService.getAll();
      const citiesData = Array.isArray(response?.data) ? response.data : [];
      setCities(citiesData);
      calculateStats(citiesData);
    } catch (err) {
      console.error('Erreur:', err);
      message.error('Erreur lors du chargement des villes');
    } finally {
      setLoading(false);
    }
  };

  const filteredCities = cities.filter((city) => {
    const matchSearch = city.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      city.code?.toLowerCase().includes(searchTerm.toLowerCase());
    if (!matchSearch) return false;
    if (dateRange && dateRange[0] && dateRange[1]) {
      const created = dayjs(city.createdAt);
      return created.isAfter(dateRange[0].startOf('day').subtract(1, 'ms')) &&
        created.isBefore(dateRange[1].endOf('day').add(1, 'ms'));
    }
    return true;
  });

  const handleAdd = () => {
    setSelectedCity(null);
    form.resetFields();
    setModalOpen(true);
  };

  const handleEdit = (record) => {
    setSelectedCity(record);
    form.setFieldsValue({
      code: record.code,
      name: record.name,
      isActive: record.isActive !== false,
      isDefault: record.isDefault || false,
      displayOrder: record.displayOrder || 0,
      apiKey: record.yandex?.apiKey || '',
      clientId: record.yandex?.clientId || '',
      parkId: record.yandex?.parkId || '',
      workRuleId: record.yandex?.workRuleId || '',
    });
    setModalOpen(true);
  };

  const handleDelete = (record) => {
    Modal.confirm({
      title: 'Supprimer la ville',
      content: (
        <div>
          <p>Voulez-vous vraiment supprimer la ville <strong>{record.name}</strong> ?</p>
          <p style={{ color: '#ff4d4f', fontSize: 12 }}>
            Cette action est irréversible.
          </p>
        </div>
      ),
      okText: 'Supprimer',
      okType: 'danger',
      cancelText: 'Annuler',
      onOk: async () => {
        try {
          const response = await citiesService.delete(record._id);
          if (response?.success) {
            message.success('Ville supprimée avec succès');
            fetchCities();
          } else {
            message.error(response?.message || 'Erreur lors de la suppression');
          }
        } catch (error) {
          message.error(error?.response?.data?.message || error?.message || 'Erreur lors de la suppression');
        }
      },
    });
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setIsSubmitting(true);

      const payload = {
        code: values.code,
        name: values.name,
        isActive: values.isActive,
        isDefault: values.isDefault,
        displayOrder: values.displayOrder || 0,
        yandex: {
          apiKey: values.apiKey,
          clientId: values.clientId,
          parkId: values.parkId,
          workRuleId: values.workRuleId || null,
        }
      };

      if (selectedCity) {
        const response = await citiesService.update(selectedCity._id, payload);
        if (response?.success) {
          message.success('Ville mise à jour avec succès');
          setModalOpen(false);
          fetchCities();
        } else {
          message.error(response?.message || 'Erreur lors de la mise à jour');
        }
      } else {
        const response = await citiesService.create(payload);
        if (response?.success) {
          message.success('Ville créée avec succès');
          setModalOpen(false);
          fetchCities();
        } else {
          message.error(response?.message || 'Erreur lors de la création');
        }
      }
    } catch (error) {
      message.error(error?.response?.data?.message || error?.message || 'Erreur');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleToggle = async (record) => {
    try {
      const response = await citiesService.toggle(record._id);
      if (response?.success) {
        message.success(response.data?.isActive ? 'Ville activée' : 'Ville désactivée');
        fetchCities();
      } else {
        message.error(response?.message || 'Erreur');
      }
    } catch (error) {
      message.error(error?.message || 'Erreur');
    }
  };

  const handleSetDefault = async (record) => {
    try {
      const response = await citiesService.setDefault(record._id);
      if (response?.success) {
        message.success(`${record.name} est maintenant la ville par défaut`);
        fetchCities();
      } else {
        message.error(response?.message || 'Erreur');
      }
    } catch (error) {
      message.error(error?.message || 'Erreur');
    }
  };

  const handleSeed = async () => {
    Modal.confirm({
      title: 'Initialiser les villes',
      content: (
        <div>
          <p>Cette action va créer les villes par défaut :</p>
          <ul style={{ marginTop: 8, paddingLeft: 20 }}>
            <li>Abidjan (par défaut)</li>
            <li>Yamoussoukro</li>
            <li>San Pedro</li>
            <li>Bouaké</li>
            <li>Sassandra</li>
            <li>Korhogo</li>
          </ul>
          <p style={{ color: '#666', marginTop: 12 }}>Les villes existantes seront ignorées.</p>
        </div>
      ),
      okText: 'Initialiser',
      cancelText: 'Annuler',
      onOk: async () => {
        const hideLoading = message.loading('Initialisation en cours...', 0);
        try {
          const response = await citiesService.seed();
          hideLoading();
          if (response?.success) {
            message.success(response.message);
            fetchCities();
          } else {
            message.error(response?.message || 'Erreur');
          }
        } catch (error) {
          hideLoading();
          message.error(error?.message || 'Erreur lors du seed');
        }
      },
    });
  };

  const columns = [
    {
      title: 'Ville',
      key: 'name',
      render: (_, record) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <GlobalOutlined style={{ fontSize: 24, color: '#fa8c16' }} />
          <div>
            <div style={{ fontWeight: 600, fontSize: 15 }}>
              {record.name}
              {record.isDefault && (
                <Tag color="gold" style={{ marginLeft: 8 }}>
                  <StarFilled /> Par défaut
                </Tag>
              )}
            </div>
            <Tag color="blue">{record.code?.toUpperCase()}</Tag>
          </div>
        </div>
      ),
      sorter: (a, b) => a.name?.localeCompare(b.name),
    },
    {
      title: 'Clés Yandex',
      key: 'yandex',
      width: 200,
      render: (_, record) => (
        <div style={{ fontSize: 12 }}>
          <Tooltip title={record.yandex?.apiKey || 'Non configuré'}>
            <Tag icon={<KeyOutlined />} color={record.yandex?.apiKey ? 'green' : 'red'}>
              API Key: {record.yandex?.apiKey ? '****' + record.yandex.apiKey.slice(-4) : 'N/A'}
            </Tag>
          </Tooltip>
          <br />
          <Tooltip title={record.yandex?.parkId || 'Non configuré'}>
            <Tag icon={<ApiOutlined />} color={record.yandex?.parkId ? 'green' : 'red'} style={{ marginTop: 4 }}>
              Park ID: {record.yandex?.parkId ? record.yandex.parkId.slice(0, 8) + '...' : 'N/A'}
            </Tag>
          </Tooltip>
        </div>
      )
    },
    {
      title: 'Ordre',
      dataIndex: 'displayOrder',
      key: 'displayOrder',
      width: 80,
      align: 'center',
      render: (order) => <Tag>{order || 0}</Tag>,
      sorter: (a, b) => (a.displayOrder || 0) - (b.displayOrder || 0),
    },
    {
      title: 'Date de création',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 150,
      render: (date) => (
        <span style={{ color: '#595959', fontSize: 12 }}>
          <CalendarOutlined style={{ marginRight: 6, color: '#fa8c16' }} />
          {date ? dayjs(date).format('DD/MM/YYYY') : '-'}
        </span>
      )
    },
    {
      title: 'Statut',
      key: 'status',
      width: 100,
      align: 'center',
      render: (_, record) => (
        <Tooltip title={record.isActive !== false ? 'Active' : 'Inactive'}>
          <Switch
            checked={record.isActive !== false}
            onChange={() => handleToggle(record)}
            checkedChildren={<CheckOutlined />}
            unCheckedChildren={<CloseOutlined />}
            size="small"
          />
        </Tooltip>
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 150,
      align: 'center',
      render: (_, record) => (
        <Space>
          {!record.isDefault && (
            <Tooltip title="Définir par défaut">
              <Button
                icon={<StarOutlined />}
                size="small"
                onClick={() => handleSetDefault(record)}
                style={{ borderRadius: 6, color: '#faad14', borderColor: '#faad14' }}
              />
            </Tooltip>
          )}
          <Tooltip title="Modifier">
            <Button
              icon={<EditOutlined />}
              size="small"
              onClick={() => handleEdit(record)}
              style={{ borderRadius: 6, color: '#1890ff', borderColor: '#1890ff' }}
            />
          </Tooltip>
          <Tooltip title="Supprimer">
            <Button
              danger
              type="text"
              icon={<DeleteOutlined />}
              size="small"
              onClick={() => handleDelete(record)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div className="content-wrapper">
      <style>
        {`
          .stat-card-orange {
            background: linear-gradient(135deg, #fa8c16 0%, #ffc069 100%);
            border-radius: 12px;
            border: none;
          }
          .stat-card-orange .ant-statistic-title,
          .stat-card-orange .ant-statistic-content {
            color: white !important;
          }
        `}
      </style>

      <div className="container-xxl flex-grow-1" style={{ paddingTop: 8, paddingLeft: 24, paddingRight: 24 }}>
        <Breadcrumb
          separator={<RightOutlined style={{ fontSize: 12, color: '#8c8c8c' }} />}
          style={{ marginBottom: 12 }}
        >
          <Breadcrumb.Item>
            <Link to="/dashboard" style={{ color: '#fa8c16' }}>Accueil</Link>
          </Breadcrumb.Item>
          <Breadcrumb.Item>
            <span style={{ color: '#595959' }}>Gestion des Villes</span>
          </Breadcrumb.Item>
        </Breadcrumb>

        {/* Statistiques */}
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={8}>
            <Card className="stat-card-orange" bodyStyle={{ padding: isMobile ? 12 : 16 }}>
              <Statistic
                title={<span><GlobalOutlined style={{ marginRight: 6 }} />Total</span>}
                value={stats.total}
                valueStyle={{ fontSize: isMobile ? 20 : 28, fontWeight: 'bold' }}
              />
            </Card>
          </Col>
          <Col xs={8}>
            <Card className="stat-card-orange" bodyStyle={{ padding: isMobile ? 12 : 16 }}>
              <Statistic
                title={<span><CheckOutlined style={{ marginRight: 6 }} />Actives</span>}
                value={stats.active}
                valueStyle={{ fontSize: isMobile ? 20 : 28, fontWeight: 'bold' }}
              />
            </Card>
          </Col>
          <Col xs={8}>
            <Card className="stat-card-orange" bodyStyle={{ padding: isMobile ? 12 : 16 }}>
              <Statistic
                title={<span><CloseOutlined style={{ marginRight: 6 }} />Inactives</span>}
                value={stats.inactive}
                valueStyle={{ fontSize: isMobile ? 20 : 28, fontWeight: 'bold' }}
              />
            </Card>
          </Col>
        </Row>

        {/* Carte principale */}
        <Card style={{ borderRadius: 16, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
          <Row gutter={[12, 12]} style={{ marginBottom: 20 }}>
            <Col xs={24} sm={8}>
              <Input
                prefix={<SearchOutlined style={{ color: '#fa8c16' }} />}
                placeholder="Rechercher une ville..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                allowClear
                size={controlSize}
                style={{ borderRadius: 8 }}
              />
            </Col>
            <Col xs={24} sm={8}>
              <DatePicker.RangePicker
                value={dateRange}
                onChange={(v) => setDateRange(v)}
                format="DD/MM/YYYY"
                placeholder={['Date début', 'Date fin']}
                style={{ width: '100%', borderRadius: 8 }}
                size={controlSize}
                allowClear
              />
            </Col>
            <Col xs={24} sm={8} style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, flexWrap: 'wrap' }}>
              <Button
                icon={<DatabaseOutlined />}
                onClick={handleSeed}
                size={controlSize}
                type="dashed"
              >
                Initialiser
              </Button>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleAdd}
                size={controlSize}
                style={{
                  background: 'linear-gradient(135deg, #fa8c16 0%, #ffc069 100%)',
                  border: 'none'
                }}
              >
                Ajouter une ville
              </Button>
            </Col>
          </Row>

          <Table
            columns={columns}
            dataSource={filteredCities.map((city) => ({ ...city, key: city._id }))}
            pagination={{ pageSize: 10, showSizeChanger: true }}
            loading={isLoading}
            size={controlSize}
            scroll={{ x: 800 }}
          />
        </Card>
      </div>

      {/* Modal Ajout/Édition */}
      <Modal
        title={
          <span>
            <GlobalOutlined style={{ marginRight: 8, color: '#fa8c16' }} />
            {selectedCity ? 'Modifier la ville' : 'Ajouter une ville'}
          </span>
        }
        open={isModalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={handleSubmit}
        okText={selectedCity ? 'Modifier' : 'Ajouter'}
        cancelText="Annuler"
        confirmLoading={isSubmitting}
        width={600}
      >
        <Form form={form} layout="vertical" initialValues={{ isActive: true, isDefault: false, displayOrder: 0 }}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Code de la ville"
                name="code"
                rules={[{ required: true, message: 'Le code est requis' }]}
                tooltip="Code unique pour identifier la ville (ex: ABJ, SPY)"
              >
                <Input placeholder="Ex: ABJ, SPY..." style={{ textTransform: 'uppercase' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Nom de la ville"
                name="name"
                rules={[{ required: true, message: 'Le nom est requis' }]}
              >
                <Input placeholder="Ex: Abidjan, San Pedro..." />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Ordre d'affichage"
                name="displayOrder"
                tooltip="Plus le nombre est petit, plus la ville apparaît en premier"
              >
                <Input type="number" min={0} placeholder="0" />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item
                label="Active"
                name="isActive"
                valuePropName="checked"
              >
                <Switch checkedChildren="Oui" unCheckedChildren="Non" />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item
                label="Par défaut"
                name="isDefault"
                valuePropName="checked"
              >
                <Switch checkedChildren="Oui" unCheckedChildren="Non" />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left" style={{ color: '#fa8c16' }}>
            <ApiOutlined /> Configuration Yandex
          </Divider>

          <Form.Item
            label="API Key"
            name="apiKey"
            rules={[{ required: true, message: 'La clé API est requise' }]}
          >
            <Input.Password placeholder="Clé API Yandex Fleet" />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Client ID"
                name="clientId"
                rules={[{ required: true, message: 'Le Client ID est requis' }]}
                tooltip="Format: taxi/park/xxxxx"
              >
                <Input placeholder="taxi/park/xxxxx" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Park ID"
                name="parkId"
                rules={[{ required: true, message: 'Le Park ID est requis' }]}
              >
                <Input placeholder="ID du parc Yandex" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            label="Work Rule ID"
            name="workRuleId"
            tooltip="Optionnel - ID de la règle de travail Yandex"
          >
            <Input placeholder="ID de la règle de travail (optionnel)" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Cities;
